import { useState, useCallback, useEffect, useRef } from 'react';
import { Eventbus } from '../utils';
import logger from '../utils/logger';

function useDataService({
  service,
  serviceUpdateEvent
}) {
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);
  const isInitializing = useRef(false); // Use ref for sync updates
  const [loading, setLoading] = useState(true);

  const updateFromService = useCallback((updatedData) => {
    // Trigger a data refresh when service data is updated
    setDataRefreshTrigger(prev => prev + 1);
  }, []);

  // Register service update event listener
  useEffect(() => {
    if (serviceUpdateEvent) {
      Eventbus.on(serviceUpdateEvent, updateFromService);

      return () => {
        Eventbus.off(serviceUpdateEvent, updateFromService);
      };
    }
  }, [serviceUpdateEvent, updateFromService]);
  
  const loadInitialData = useCallback(async (dateRange = null) => {
    // Prevent duplicate initialization calls
    if (isInitializing.current) {
      logger.log('Already initializing data, skipping duplicate call');
      return;
    }
    isInitializing.current = true;
    setLoading(true);

    try {
      if (service && service.loadData && typeof service.loadData === 'function') {
        if (dateRange && dateRange.from && dateRange.to) {
          await service.loadData(true, dateRange.from, dateRange.to);
        } else {
          await service.loadData(true);
        }
        setDataRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      logger.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
      isInitializing.current = false;
    }
  }, [service, logger]);
  const refreshData = useCallback(async (forceRefresh = false, dateRange = null) => {
    setLoading(true);
    try {
      if (service && service.loadData && typeof service.loadData === 'function') {
        if (dateRange && dateRange.from && dateRange.to) {
          await service.loadData(forceRefresh, dateRange.from, dateRange.to);
        } else {
          await service.loadData(forceRefresh);
        }
        setDataRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      logger.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  }, [service]);
  
  const getData = useCallback(() => {
    return (service && service.getData) ? service.getData() : [];
  }, [service]); // Remove dataRefreshTrigger to prevent circular dependency

  const triggerDataRefresh = useCallback(() => {
    setDataRefreshTrigger(prev => prev + 1);
  }, []);
  // Use a combination of hook loading state and service loading state
  const getActualLoadingState = useCallback(() => {
    const serviceLoading = service?.getLoadingState?.() ?? false;
    return loading || serviceLoading;
  }, [loading, service]); // Remove dataRefreshTrigger to prevent circular dependency

  return {
    loading: getActualLoadingState(),
    dataRefreshTrigger,
    loadInitialData,
    refreshData,
    getData,
    triggerDataRefresh
  };
}

export default useDataService;
